import type { Snapshot } from '@amoeba/shared';

export type SnapshotStatus = 'loading' | 'live' | 'stale' | 'offline';

export interface FetchOptions {
  /** Snapshot URL. Defaults to `./snapshot.json`. */
  readonly url?: string;
  /** Poll interval in milliseconds. Defaults to 30_000. */
  readonly intervalMs?: number;
  /** Age after which a snapshot is considered stale. Defaults to 5 minutes. */
  readonly staleMs?: number;
  /** Called on every poll attempt with the current snapshot (if any) and status. */
  readonly onUpdate: (snapshot: Snapshot | null, status: SnapshotStatus) => void;
}

/**
 * Poll a snapshot.json endpoint.
 *
 * Returns a stop function. The first fetch runs immediately. Errors and stale
 * data are surfaced through `onUpdate` with a `null` snapshot / `offline` or
 * `stale` status so the UI never crashes on a missing brain exporter.
 */
export function startPolling(options: FetchOptions): () => void {
  const url = options.url ?? './snapshot.json';
  const intervalMs = options.intervalMs ?? 30_000;
  const staleMs = options.staleMs ?? 5 * 60 * 1_000;

  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function tick(): Promise<void> {
    if (stopped) return;

    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const snapshot = (await response.json()) as Snapshot;
      const generatedAt = snapshot.generatedAt ?? Date.now();
      const ageMs = Date.now() - generatedAt;
      const status: SnapshotStatus = ageMs > staleMs ? 'stale' : 'live';
      options.onUpdate(snapshot, status);
    } catch {
      options.onUpdate(null, 'offline');
    }

    if (!stopped) {
      timer = setTimeout(tick, intervalMs);
    }
  }

  tick();

  return () => {
    stopped = true;
    if (timer !== null) {
      clearTimeout(timer);
    }
  };
}
